import { useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';

/** 约 1 行完整 + 半行露出 */
const COLLAPSED_MAX_PX = 68;

type SiteItem = { id: number; site_name: string };

type Props = {
  sites: SiteItem[];
  currentSiteId: number;
  allSitesMode: boolean;
  onSwitch: (siteId: number) => void;
};

const chipClass = (active: boolean) =>
  cn(
    'rounded-full px-4 py-2 text-base font-semibold transition-colors',
    active ? 'bg-teal-600 text-white shadow-sm' : 'bg-slate-100 text-slate-700 active:bg-slate-200',
  );

function WideChevron({ up = false }: { up?: boolean }) {
  return (
    <svg
      width="72"
      height="14"
      viewBox="0 0 72 14"
      className={cn('block shrink-0 text-slate-500 transition-transform', up && 'rotate-180')}
      aria-hidden
    >
      <path
        d="M6 4 L36 11 L66 4"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function SiteMasterSiteSwitcher({ sites, currentSiteId, allSitesMode, onSwitch }: Props) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;

    const measure = () => {
      const prevMax = el.style.maxHeight;
      const prevOverflow = el.style.overflow;
      el.style.maxHeight = 'none';
      el.style.overflow = 'visible';
      const fullHeight = el.scrollHeight;
      el.style.maxHeight = prevMax;
      el.style.overflow = prevOverflow;
      setOverflows(fullHeight > COLLAPSED_MAX_PX + 2);
    };

    measure();
    const raf1 = requestAnimationFrame(measure);
    let raf2 = 0;
    const rafOuter = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(measure);
    });

    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(rafOuter);
      cancelAnimationFrame(raf2);
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [sites]);

  useLayoutEffect(() => {
    if (!overflows) setExpanded(false);
  }, [overflows, sites]);

  const collapsed = overflows && !expanded;
  const totalCount = sites.length + 1;

  return (
    <div className="mt-3">
      <div className={cn('relative', collapsed && 'pb-7')}>
        <div
          ref={wrapRef}
          className={cn(
            'flex flex-wrap gap-2 transition-[max-height] duration-300 ease-out',
            collapsed && 'overflow-hidden',
          )}
          style={collapsed ? { maxHeight: `${COLLAPSED_MAX_PX}px` } : undefined}
        >
          <button type="button" onClick={() => onSwitch(0)} className={chipClass(allSitesMode)}>
            全部
          </button>
          {sites.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => onSwitch(s.id)}
              className={chipClass(!allSitesMode && currentSiteId === s.id)}
            >
              {s.site_name}
            </button>
          ))}
        </div>

        {overflows ? (
          <>
            {collapsed ? (
              <div
                className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-8 bg-gradient-to-t from-white from-50% via-white/70 to-transparent"
                aria-hidden
              />
            ) : null}
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              aria-label={expanded ? '收起站点列表' : `展开全部 ${totalCount} 个站点`}
              className={cn(
                'z-[2] flex w-full items-center justify-center text-slate-500 active:text-teal-600',
                collapsed
                  ? 'absolute inset-x-0 bottom-0 h-7'
                  : 'mt-1 min-h-[28px] py-0.5',
              )}
            >
              <WideChevron up={expanded} />
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}
